import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Skeleton, cn } from '@mlm/ui';
import { Plus, Trash2 } from 'lucide-react';
import { listMemberships } from '../../lib/api';
import { NumberField, SelectField, TextField, ToggleField } from './fields';

export type BinaryConfig = {
  pairing: {
    /** Points each leg must accrue to form one pair. */
    unit_points?: number;
    /** @deprecated legacy alias for unit_points; auto-migrated on next save. */
    unit_pv?: number;
    pay_per_pair: number;
    /**
     * Multiple symmetric pairing combinations. When non-empty they REPLACE the
     * single points/pay-per-pair rule and are matched greedy largest-first.
     */
    combinations?: Array<{ points: number; payout: number }>;
  };
  cycle: { cycles: 1 | 2; daily_cap: number; max_pairs_per_day?: number };
  /**
   * Pair-drop milestones — each row fires on every Nth paid pair (N=1 = every
   * pair). A row may pay a `cash` and/or `gc` bonus per crossing, and may alter
   * the standard pairing payout on its trigger pairs (`pairing_on_trigger`). N
   * must be unique per row.
   */
  pair_drops?: Array<{
    n: number;
    cash?: number;
    gc?: number;
    cash_capped?: boolean;
    pairing_on_trigger?: 'keep' | 'suppress' | 'to_gc';
  }>;
  max_strong_leg?: number;
  /** Burn the strong-leg carryover to 0 once the daily pair cap is reached. */
  reset_strong_leg_on_max_pairs?: boolean;
  timezone?: string;
  /**
   * Per-ancestor point lookup `points_matrix[receiver tier][buyer tier]`,
   * applied on PLACEMENTS. Binary points are tracked separately from product
   * PV — order PV does NOT contribute to binary legs. A missing cell = zero
   * points for that (receiver, buyer) tier pair.
   */
  points_matrix?: Record<string, Record<string, number>>;
  /**
   * ALL variants: opt-in repurchase leg points. On order_completed each binary
   * ancestor's leg gains SUM(qty × product.binary_points). Off (default) =
   * only placements feed the legs (teslab: placements + its distribution flow).
   */
  product_points_on_purchase?: boolean;
  /**
   * Mentor's Bonus rider. When enabled, an earning ancestor's sponsor-tree
   * uplines earn a percentage of the binary cash that ancestor earned, keyed on
   * the mentor's membership tier (`tiers[mentorCode]`) and gated by per-level
   * min-directs. Legacy variant only.
   */
  mentor_bonus?: {
    enabled?: boolean;
    tiers?: Record<
      string,
      {
        amount?: Record<string, number>;
        requirement?: Record<string, number>;
      }
    >;
    /**
     * Optional income cap with auto-flushout. `amount` is the ceiling on a
     * mentor's accumulated Mentor's Bonus over `window` ('daily' | 'lifetime');
     * the excess flushes. 0 / unset = no cap.
     */
    cap?: { amount: number; window: 'daily' | 'lifetime' };
  };
  // --- Teslab variant extras (ignored by the legacy variant) ---
  /** Item-PV gate: a slot pays in a run only if its window PPV >= this. 0 = no gate. */
  qualification?: { required_ppv?: number };
  /** Burn strong-leg PV above max_strong_leg after pairing (with retention on). */
  total_flushout?: boolean;
  /**
   * Keep the stronger leg's remainder after a distribution pairs (carryover).
   * Default true. false = both legs reset to 0 each distribution (use-it-or-lose-it).
   */
  strong_leg_retention?: boolean;
  /**
   * Per-RECEIVER-tier pairing payout %: paired points × pct/100. A tier with no
   * entry (or 0) banks its legs but earns no pairing income that run.
   */
  pairing_pct_by_tier?: Record<string, number>;
  /**
   * Teslab Mentor's Bonus rider. The PERCENTAGE is per (mentor tier × sponsor
   * level) — it can rise with depth (level 2 may pay more than the direct
   * level 1). Each cell also carries the min active directs needed to unlock
   * that level ("depth depends on directs").
   */
  teslab_mentor?: {
    enabled?: boolean;
    tiers?: Record<string, { levels?: Record<string, { pct?: number; min_directs?: number }> }>;
  };
  // --- Extreme variant extras ---
  /**
   * Extreme variant: minimum active directly-sponsored slots a member needs
   * before they may change their binary L/R side preference. 0 = no gate. A
   * membership tier overrides this via its per-tier `binary_direct_requirements`.
   */
  direct_requirements?: number;
};

/**
 * Variant-envelope stored shape. The framework reads `variant_id` to dispatch
 * to the matching variant under `packages/compplans/src/plans/binary/variants/`.
 * Legacy rows without an envelope are unwrapped transparently — the whole
 * stored object is treated as the legacy variant's config.
 */
export type BinaryStoredConfig = {
  variant_id?: string;
  variant_config?: BinaryConfig;
} & Partial<BinaryConfig>;

type BinaryVariantOption = {
  id: string;
  label: string;
  defaultConfig: () => BinaryConfig;
};

/**
 * Registered Binary variants for the UI. Each entry pairs a variant id (must
 * match `packages/compplans/src/plans/binary/index.ts` registration) with the
 * editor's default config. Adding a per-client variant means appending one
 * row here and a `case` in `renderVariantForm` below.
 */
export const BINARY_VARIANTS: BinaryVariantOption[] = [
  { id: 'legacy', label: 'Legacy Binary', defaultConfig: emptyBinaryConfig },
  {
    id: 'teslab',
    label: 'Teslab (distribution-driven pairing)',
    defaultConfig: emptyTeslabConfig,
  },
  {
    id: 'extreme',
    label: 'Extreme Binary (per-tier pairing tables)',
    defaultConfig: emptyExtremeConfig,
  },
];

function unwrapStored(stored: BinaryStoredConfig): {
  variantId: string;
  config: BinaryConfig;
} {
  const variantId = stored.variant_id ?? 'legacy';
  if (stored.variant_config) {
    return {
      variantId,
      config: { ...emptyBinaryConfig(), ...stored.variant_config },
    };
  }
  const { variant_id: _vid, variant_config: _vc, ...rest } = stored;
  void _vid;
  void _vc;
  return {
    variantId,
    config: { ...emptyBinaryConfig(), ...(rest as Partial<BinaryConfig>) },
  };
}

function wrapStored(variantId: string, config: BinaryConfig): BinaryStoredConfig {
  return { variant_id: variantId, variant_config: config };
}

export function BinaryEditor({
  value,
  onChange,
  readOnly,
}: {
  value: BinaryStoredConfig;
  onChange: (next: BinaryStoredConfig) => void;
  readOnly?: boolean;
}) {
  const { variantId, config } = unwrapStored(value);
  const updateConfig = (next: BinaryConfig) => onChange(wrapStored(variantId, next));
  const setVariant = (nextId: string) => {
    const variant = BINARY_VARIANTS.find((v) => v.id === nextId);
    if (!variant) return;
    onChange(wrapStored(nextId, variant.defaultConfig()));
  };

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h3 className="text-sm font-medium">Variant</h3>
        <p className="text-[10px] text-muted-foreground -mt-1">
          Selects which Binary implementation runs. Changing the variant replaces the config with
          that variant's defaults. Publish a new version to save.
        </p>
        <div className="max-w-md">
          <SelectField
            label="Active variant"
            value={variantId}
            disabled={readOnly || BINARY_VARIANTS.length <= 1}
            onChange={(v) => setVariant(v)}
            options={BINARY_VARIANTS.map((v) => ({ value: v.id, label: v.label }))}
          />
        </div>
      </section>

      {renderVariantForm(variantId, config, updateConfig, readOnly)}
    </div>
  );
}

function renderVariantForm(
  variantId: string,
  config: BinaryConfig,
  onChange: (next: BinaryConfig) => void,
  readOnly: boolean | undefined,
) {
  switch (variantId) {
    case 'legacy':
      return (
        <LegacyBinaryForm
          value={config}
          onChange={onChange}
          readOnly={readOnly}
          showMentorBonus
          singleCycleOnly
        />
      );
    case 'teslab':
      return <TeslabBinaryForm value={config} onChange={onChange} readOnly={readOnly} />;
    case 'extreme':
      return <ExtremeBinaryForm value={config} onChange={onChange} readOnly={readOnly} />;
    default:
      return (
        <p className="text-xs text-muted-foreground border rounded-md p-4">
          No editor registered for variant <code className="font-mono">{variantId}</code>. Add one
          to <code className="font-mono">BINARY_VARIANTS</code> and the switch in{' '}
          <code className="font-mono">binary-editor.tsx</code>.
        </p>
      );
  }
}

function LegacyBinaryForm({
  value,
  onChange,
  readOnly,
  showMentorBonus,
  perTierPairing,
  showDirectRequirements,
  singleCycleOnly,
}: {
  value: BinaryConfig;
  onChange: (next: BinaryConfig) => void;
  readOnly?: boolean;
  /** Only the legacy variant runs the mentor rider, so only it shows the editor. */
  showMentorBonus?: boolean;
  /**
   * Extreme variant: pairing is defined PER MEMBERSHIP (in the Memberships /
   * per-tier editor), so hide the flat pairing table, the single-rule fallback,
   * the pair-drop milestones, and the strong-leg-reset toggle from this
   * plan-level form. The per-tier editor owns pairing + its GC riders +
   * total_flushout for each tier.
   */
  perTierPairing?: boolean;
  /**
   * Extreme variant: the directs-to-unlock side-preference gate is part of the
   * comp plan (not Tenant Settings), so show it here.
   */
  showDirectRequirements?: boolean;
  /**
   * Legacy/Teslab run one cycle per day, so offer only that. Extreme omits this
   * to expose the 1|2 (12h) selector.
   */
  singleCycleOnly?: boolean;
}) {
  return (
    <div className="space-y-6">
      {showDirectRequirements ? (
        <section className="space-y-3">
          <h3 className="text-sm font-medium">Side preference gate</h3>
          <p className="text-[10px] text-muted-foreground -mt-2">
            Minimum active directly-sponsored slots a member needs before they may change their
            binary L/R side preference. <strong>0</strong> = no gate. A membership tier can override
            this in the Memberships editor (per-tier{' '}
            <span className="font-mono">binary_direct_requirements</span>).
          </p>
          <NumberField
            label="Directs to unlock side change"
            hint="Default 2. The member's side preference is locked until they have this many active directs."
            value={value.direct_requirements ?? 2}
            disabled={readOnly}
            onChange={(v) =>
              onChange({
                ...value,
                direct_requirements: v == null ? undefined : Math.max(0, Math.floor(v)),
              })
            }
            min={0}
            step={1}
          />
        </section>
      ) : null}

      {!perTierPairing ? (
        <>
          <section className="space-y-3">
            <h3 className="text-sm font-medium">Pairing combinations</h3>
            <p className="text-[10px] text-muted-foreground -mt-2">
              Each combination needs that many points on BOTH legs to form one pair. Applied
              largest-first (200:200 = one ₱200 pair, not two ₱100 pairs) and replaces the single
              Points/Pay-per-pair below when set. Leave empty to use the single rule.
            </p>
            <PairingCombinationsEditor
              value={value.pairing.combinations ?? []}
              disabled={readOnly}
              onChange={(next) =>
                onChange({
                  ...value,
                  pairing: {
                    ...value.pairing,
                    combinations: next.length > 0 ? next : undefined,
                  },
                })
              }
            />
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-medium">Pairing (single rule — fallback)</h3>
            <p className="text-[10px] text-muted-foreground -mt-2">
              Used only when no combinations are configured above.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumberField
                label="Points per pair"
                hint="Pairing consumes this many points from each leg per pair."
                value={value.pairing.unit_points ?? value.pairing.unit_pv ?? 0}
                disabled={readOnly}
                onChange={(v) => {
                  // Always write the canonical key; drop the legacy one so the
                  // next save fully migrates the row.
                  const { unit_pv: _legacy, ...rest } = value.pairing;
                  void _legacy;
                  onChange({
                    ...value,
                    pairing: { ...rest, unit_points: v ?? 0 },
                  });
                }}
                min={0}
                step={1}
              />
              <NumberField
                label="Pay per pair"
                hint="Cash paid to the ancestor each pair."
                value={value.pairing.pay_per_pair}
                disabled={readOnly}
                onChange={(v) =>
                  onChange({
                    ...value,
                    pairing: { ...value.pairing, pay_per_pair: v ?? 0 },
                  })
                }
                min={0}
                step={0.01}
              />
            </div>
          </section>
        </>
      ) : null}

      <section className="space-y-3">
        <h3 className="text-sm font-medium">Points matrix</h3>
        <p className="text-[10px] text-muted-foreground -mt-2">
          Per-ancestor point lookup keyed on (receiver tier × buyer tier), applied when a slot is
          placed in the binary tree. A blank cell means zero points for that pair. Product
          repurchases feed the legs only via the toggle below.
        </p>
        <PointsMatrixEditor
          value={value.points_matrix ?? {}}
          disabled={readOnly}
          onChange={(next) => onChange({ ...value, points_matrix: next })}
        />
        <ToggleField
          label="Credit product binary points on repurchase"
          hint="On order_completed, each binary ancestor's leg gains SUM(qty × product.binary_points) and pairs through the same combinations and daily caps. Off = only placements feed the legs."
          disabled={readOnly}
          value={value.product_points_on_purchase ?? false}
          onChange={(on) => onChange({ ...value, product_points_on_purchase: on })}
        />
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-medium">Cycle & cap</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SelectField
            label="Cycles per day"
            hint={
              singleCycleOnly
                ? 'This variant runs one cycle per day (caps reset at tenant midnight).'
                : '2 cycles = per-day caps (income + max pairs) reset every 12 hours, splitting the tenant-local day at noon.'
            }
            value={String(value.cycle.cycles) as '1' | '2'}
            disabled={readOnly}
            onChange={(v) =>
              onChange({
                ...value,
                cycle: { ...value.cycle, cycles: Number(v) as 1 | 2 },
              })
            }
            options={
              singleCycleOnly
                ? [{ value: '1', label: 'Once per day' }]
                : [
                    { value: '1', label: '1 cycle (daily)' },
                    { value: '2', label: '2 cycles (12-hour)' },
                  ]
            }
          />
          <NumberField
            label="Daily cash cap"
            hint="Per-slot per-day cap on pairing income. The boundary pair partial-pays to the cap exactly; the flushed remainder is logged (PV still consumed). 0 = unlimited."
            value={value.cycle.daily_cap}
            disabled={readOnly}
            onChange={(v) =>
              onChange({
                ...value,
                cycle: { ...value.cycle, daily_cap: v ?? 0 },
              })
            }
            min={0}
            step={1}
          />
          <NumberField
            label="Max pairs per day"
            hint="Per-slot per-day cap on PAID pairs. Pairs beyond it are flushed (PV consumed, no pay) — same as the cash cap. 0 = unlimited."
            value={value.cycle.max_pairs_per_day ?? 0}
            disabled={readOnly}
            onChange={(v) =>
              onChange({
                ...value,
                cycle: { ...value.cycle, max_pairs_per_day: v ?? 0 },
              })
            }
            min={0}
            step={1}
          />
          <NumberField
            label="Max strong leg"
            hint="Optional cap on stronger-leg PV considered for pair calc. 0 = no cap."
            value={value.max_strong_leg ?? 0}
            disabled={readOnly}
            onChange={(v) => onChange({ ...value, max_strong_leg: v ?? 0 })}
            min={0}
            step={1}
          />
        </div>
        {/* Legacy strong-leg-only reset. Extreme's flushout is per-membership
            (`total_flushout` lives in the Memberships / per-tier editor), so
            this plan-level toggle is hidden for the extreme variant. */}
        {!perTierPairing ? (
          <ToggleField
            label="Reset strong leg at max pairs/day"
            hint="When a slot reaches its daily paid-pair cap, burn the remaining strong-leg PV to 0 — carryover does not roll to the next day. Requires Max pairs per day > 0."
            disabled={readOnly}
            value={value.reset_strong_leg_on_max_pairs ?? false}
            onChange={(on) => onChange({ ...value, reset_strong_leg_on_max_pairs: on })}
          />
        ) : null}
      </section>

      {!perTierPairing ? (
        <section className="space-y-3">
          <h3 className="text-sm font-medium">Pair drops (milestone bonuses)</h3>
          <p className="text-[10px] text-muted-foreground -mt-2">
            Each row fires on every Nth PAID pair (N=1 = every pair). It can pay a cash and/or GC
            bonus, and can change what happens to the normal pairing payout on that pair. Leave
            empty for no milestone bonuses. N must be unique per row.
          </p>
          <PairDropsEditor
            value={value.pair_drops ?? []}
            disabled={readOnly}
            onChange={(next) =>
              onChange({ ...value, pair_drops: next.length > 0 ? next : undefined })
            }
          />
        </section>
      ) : null}

      {showMentorBonus ? (
        <section className="space-y-3">
          <h3 className="text-sm font-medium">Mentor's bonus</h3>
          <p className="text-[10px] text-muted-foreground -mt-2">
            When on, each ancestor who earns binary cash also pays their SPONSOR-tree uplines
            (direct = Level 1, indirect = Level 2/3…) a percentage of that cash. Rates are keyed on
            the <strong>mentor's</strong> membership tier; an optional min-directs requirement gates
            each level. Base = all binary cash earned (pairing + pair-drop cash; GC excluded).
          </p>
          <ToggleField
            label="Enable mentor's bonus"
            hint="Off by default. Saves with the Binary config version."
            disabled={readOnly}
            value={value.mentor_bonus?.enabled ?? false}
            onChange={(on) =>
              onChange({
                ...value,
                mentor_bonus: { ...(value.mentor_bonus ?? {}), enabled: on },
              })
            }
          />
          {value.mentor_bonus?.enabled ? (
            <>
              <MentorBonusMatrixEditor
                value={value.mentor_bonus.tiers ?? {}}
                disabled={readOnly}
                onChange={(next) =>
                  onChange({
                    ...value,
                    mentor_bonus: { ...(value.mentor_bonus ?? {}), tiers: next },
                  })
                }
              />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <NumberField
                  label="Income cap"
                  hint="Ceiling on a mentor's accumulated Mentor's Bonus over the window below. Excess flushes (auto-flushout). 0 = no cap."
                  disabled={readOnly}
                  min={0}
                  step={0.01}
                  value={value.mentor_bonus?.cap?.amount ?? 0}
                  onChange={(v) =>
                    onChange({
                      ...value,
                      mentor_bonus: {
                        ...(value.mentor_bonus ?? {}),
                        cap: {
                          amount: v ?? 0,
                          window: value.mentor_bonus?.cap?.window ?? 'daily',
                        },
                      },
                    })
                  }
                />
                <SelectField
                  label="Cap window"
                  hint="`daily` resets at the tenant day boundary; `lifetime` never resets. Only applies when the cap above is greater than 0."
                  disabled={readOnly || !((value.mentor_bonus?.cap?.amount ?? 0) > 0)}
                  value={value.mentor_bonus?.cap?.window ?? 'daily'}
                  onChange={(window) =>
                    onChange({
                      ...value,
                      mentor_bonus: {
                        ...(value.mentor_bonus ?? {}),
                        cap: {
                          amount: value.mentor_bonus?.cap?.amount ?? 0,
                          window: window as 'daily' | 'lifetime',
                        },
                      },
                    })
                  }
                  options={[
                    { value: 'daily', label: 'Daily' },
                    { value: 'lifetime', label: 'Lifetime' },
                  ]}
                />
              </div>
            </>
          ) : null}
        </section>
      ) : null}

      <section className="space-y-3">
        <h3 className="text-sm font-medium">Timezone</h3>
        <div className="max-w-md">
          <TextField
            label="IANA timezone"
            hint="Used for daily cap reset. Example: Asia/Manila."
            value={value.timezone ?? ''}
            disabled={readOnly}
            onChange={(v) => onChange({ ...value, timezone: v || undefined })}
            placeholder="Asia/Manila"
          />
        </div>
      </section>
    </div>
  );
}

/**
 * Extreme Binary flat form. Mechanically it's the realtime Legacy binary
 * (same pairing combinations, points matrix, cycle/cap section, pair drops and
 * Mentor's Bonus rider) with one extra global knob — `total_flushout` — and the
 * cycle selector locked to one cycle/day (the engine throws on cycles≠1 today).
 * Per-tier pairing tables live under the Binary per-tier editor. Reuses
 * `LegacyBinaryForm` so all sub-editors stay in one place.
 */
function ExtremeBinaryForm({
  value,
  onChange,
  readOnly,
}: {
  value: BinaryConfig;
  onChange: (next: BinaryConfig) => void;
  readOnly?: boolean;
}) {
  return (
    <LegacyBinaryForm
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      showMentorBonus
      perTierPairing
      showDirectRequirements
    />
  );
}

function TeslabBinaryForm({
  value,
  onChange,
  readOnly,
}: {
  value: BinaryConfig;
  onChange: (next: BinaryConfig) => void;
  readOnly?: boolean;
}) {
  const mentor = value.teslab_mentor ?? {};
  return (
    <div className="space-y-6">
      <section className="space-y-3 border rounded-md p-4 bg-muted/20">
        <h3 className="text-sm font-medium">Teslab settings</h3>
        <p className="text-[10px] text-muted-foreground -mt-2">
          Distribution-driven binary. Leg points only ACCUMULATE in realtime (placement matrix +
          product repurchase points). Pairing and payout happen when an admin runs a binary
          distribution — they pick only the <span className="font-medium">date window</span>; the{' '}
          <span className="font-medium">pairing %</span> (per membership, below) and{' '}
          <span className="font-medium">mentor %</span> (per tier × level, below) come from this
          config. The per-distribution income cap is per-membership (set it under Members →
          Memberships).
        </p>
        <ToggleField
          label="Credit product binary points on repurchase"
          hint="On order_completed, each ancestor's leg gains SUM(qty × product.binary_points). Off = only placement matrix points apply."
          disabled={readOnly}
          value={value.product_points_on_purchase ?? false}
          onChange={(on) => onChange({ ...value, product_points_on_purchase: on })}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NumberField
            label="Required personal PV to receive income"
            hint="A slot below this PV over the distribution window banks its legs but earns no pairing income that run. 0 = no gate."
            value={value.qualification?.required_ppv ?? 0}
            disabled={readOnly}
            onChange={(v) => onChange({ ...value, qualification: { required_ppv: v ?? 0 } })}
            min={0}
            step={1}
          />
          <NumberField
            label="Max strong leg"
            hint="Optional cap on stronger-leg PV after pairing (used with Total flushout). 0 = no cap."
            value={value.max_strong_leg ?? 0}
            disabled={readOnly}
            onChange={(v) => onChange({ ...value, max_strong_leg: v ?? 0 })}
            min={0}
            step={1}
          />
        </div>
        <ToggleField
          label="Strong leg retention (carryover)"
          hint="On: after pairing, the stronger leg keeps its remainder for the next distribution. Off: both legs reset to 0 each distribution (use-it-or-lose-it)."
          disabled={readOnly}
          value={value.strong_leg_retention ?? true}
          onChange={(on) => onChange({ ...value, strong_leg_retention: on })}
        />
        <ToggleField
          label="Total flushout (burn strong-leg excess)"
          hint="With retention on, after pairing burn strong-leg PV above 'Max strong leg' so it can't carry forward. Requires Max strong leg > 0."
          disabled={readOnly}
          value={value.total_flushout ?? false}
          onChange={(on) => onChange({ ...value, total_flushout: on })}
        />
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-medium">Points matrix</h3>
        <p className="text-[10px] text-muted-foreground -mt-2">
          Per-ancestor placement-point lookup keyed on (receiver tier × buyer tier). Used on slot
          placement; product repurchases use the product's binary points. A blank cell means zero
          points for that pair.
        </p>
        <PointsMatrixEditor
          value={value.points_matrix ?? {}}
          disabled={readOnly}
          onChange={(next) => onChange({ ...value, points_matrix: next })}
        />
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-medium">Pairing %</h3>
        <p className="text-[10px] text-muted-foreground -mt-2">
          Per-membership conversion rate at distribution: a slot's payout is its paired points ×
          this %, by the slot's own tier. A tier left blank (or 0) banks its legs but earns no
          pairing income. Income is still clamped to the tier's per-distribution cap (Members →
          Memberships).
        </p>
        <PairingPctByTierEditor
          value={value.pairing_pct_by_tier ?? {}}
          disabled={readOnly}
          onChange={(next) => onChange({ ...value, pairing_pct_by_tier: next })}
        />
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-sm font-medium">Mentor's Bonus</h3>
          <ToggleField
            label="Enabled"
            disabled={readOnly}
            value={mentor.enabled ?? false}
            onChange={(on) => onChange({ ...value, teslab_mentor: { ...mentor, enabled: on } })}
          />
        </div>
        <p className="text-[10px] text-muted-foreground -mt-2">
          When a slot earns pairing cash in a distribution, its sponsor uplines earn a{' '}
          <span className="font-medium">% of that cash by their tier and sponsor level</span> —
          deeper levels may pay a higher % than the direct (level 1). Each level also has a minimum
          active directs needed to unlock it. A blank/absent level means that tier doesn't earn
          there.
        </p>
        {mentor.enabled ? (
          <TeslabMentorDepthEditor
            value={mentor.tiers ?? {}}
            disabled={readOnly}
            onChange={(tiers) => onChange({ ...value, teslab_mentor: { ...mentor, tiers } })}
          />
        ) : (
          <p className="text-xs text-muted-foreground border rounded-md p-3">
            Mentor's Bonus is off. Enable it to configure depth-by-directs.
          </p>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-medium">Timezone</h3>
        <TextField
          label="IANA timezone"
          hint="Used for date-window boundaries (e.g. Asia/Manila)."
          value={value.timezone ?? ''}
          disabled={readOnly}
          onChange={(v) => onChange({ ...value, timezone: v })}
        />
      </section>
    </div>
  );
}

/**
 * Teslab mentor depth matrix: per (mentor tier × sponsor level), the MIN active
 * directs the mentor must hold to earn at that level. A cell present (even 0)
 * means the tier earns at that level; blank means it doesn't. The percentage is
 * NOT here — it's entered per distribution run.
 */
type MentorLevels = Record<string, { pct?: number; min_directs?: number }>;
type MentorTiersByLevel = Record<string, { levels?: MentorLevels }>;

function TeslabMentorDepthEditor({
  value,
  onChange,
  disabled,
}: {
  value: MentorTiersByLevel;
  onChange: (next: MentorTiersByLevel) => void;
  disabled?: boolean;
}) {
  const tiersQ = useQuery({ queryKey: ['admin-memberships'], queryFn: listMemberships });

  const presentLevels = useMemo(() => {
    const set = new Set<number>();
    for (const tier of Object.values(value)) {
      for (const k of Object.keys(tier.levels ?? {})) {
        const n = Number(k);
        if (Number.isFinite(n) && n >= 1) set.add(n);
      }
    }
    if (set.size === 0) set.add(1);
    return Array.from(set).sort((a, b) => a - b);
  }, [value]);

  const [extra, setExtra] = useState<number[]>([]);
  const levels = useMemo(() => {
    const set = new Set<number>(presentLevels);
    for (const n of extra) set.add(n);
    return Array.from(set).sort((a, b) => a - b);
  }, [presentLevels, extra]);
  const [active, setActive] = useState<number>(levels[0] ?? 1);

  if (tiersQ.isLoading) return <Skeleton className="h-40 w-full" />;
  const tiers = tiersQ.data ?? [];
  if (tiers.length === 0) {
    return (
      <p className="text-xs text-muted-foreground border rounded-md p-4">
        No membership tiers configured yet. Create them under Members → Memberships first.
      </p>
    );
  }

  // Whether this (tier, level) cell earns at all (key present).
  function earns(code: string, level: number): boolean {
    return value[code]?.levels?.[String(level)] !== undefined;
  }
  function getCell(code: string, level: number): { pct: number; min_directs: number } {
    const v = value[code]?.levels?.[String(level)];
    return { pct: Number(v?.pct) || 0, min_directs: Number(v?.min_directs) || 0 };
  }
  function writeCell(
    code: string,
    level: number,
    present: boolean,
    cell: { pct: number; min_directs: number },
  ) {
    const next: MentorTiersByLevel = {};
    for (const c of Object.keys(value)) {
      next[c] = { levels: { ...(value[c]!.levels ?? {}) } };
    }
    if (!next[code]) next[code] = { levels: {} };
    const map = { ...(next[code]!.levels ?? {}) };
    if (!present) delete map[String(level)];
    else map[String(level)] = { pct: cell.pct, min_directs: cell.min_directs };
    next[code]!.levels = map;
    for (const c of Object.keys(next)) {
      if (next[c]!.levels && Object.keys(next[c]!.levels!).length === 0) delete next[c];
    }
    onChange(next);
  }

  function addLevel() {
    const nextLevel = Math.max(...levels, 0) + 1;
    setExtra((prev) => [...prev, nextLevel]);
    setActive(nextLevel);
  }
  function removeLevel(level: number) {
    for (const t of tiers) writeCell(t.code, level, false, { pct: 0, min_directs: 0 });
    setExtra((prev) => prev.filter((l) => l !== level));
    if (active === level) setActive(levels.filter((l) => l !== level)[0] ?? 1);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 flex-wrap">
        {levels.map((lvl) => (
          <button
            key={lvl}
            type="button"
            onClick={() => setActive(lvl)}
            className={cn(
              'h-8 px-3 rounded-md text-xs font-mono border transition-colors',
              active === lvl
                ? 'bg-primary/10 border-primary text-primary'
                : 'border-input text-muted-foreground hover:bg-muted',
            )}
          >
            {lvl === 1 ? 'Level 1 (direct)' : `Level ${lvl}`}
          </button>
        ))}
        {!disabled ? (
          <>
            <Button size="sm" variant="outline" onClick={addLevel}>
              <Plus className="h-3 w-3 mr-1" />
              Level
            </Button>
            {levels.length > 1 ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removeLevel(active)}
                className="text-destructive"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Remove L{active}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-left text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Mentor tier</th>
              <th className="px-3 py-2 font-medium">Earns at this level</th>
              <th className="px-3 py-2 font-medium">%</th>
              <th className="px-3 py-2 font-medium">Min directs</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((t) => {
              const present = earns(t.code, active);
              const cell = getCell(t.code, active);
              return (
                <tr key={t.code} className="border-t">
                  <td className="px-3 py-1.5 text-xs font-mono" title={t.name}>
                    {t.code}
                  </td>
                  <td className="px-3 py-1.5">
                    <input
                      type="checkbox"
                      className="h-4 w-4 align-middle disabled:opacity-50"
                      checked={present}
                      disabled={disabled}
                      onChange={(e) => writeCell(t.code, active, e.target.checked, cell)}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      type="number"
                      className="h-8 w-20 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                      value={cell.pct}
                      disabled={disabled || !present}
                      step={0.01}
                      min={0}
                      max={100}
                      onChange={(e) =>
                        writeCell(t.code, active, true, {
                          ...cell,
                          pct: Number(e.target.value) || 0,
                        })
                      }
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      type="number"
                      className="h-8 w-24 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                      value={cell.min_directs}
                      disabled={disabled || !present}
                      step={1}
                      min={0}
                      onChange={(e) =>
                        writeCell(t.code, active, true, {
                          ...cell,
                          min_directs: Number(e.target.value) || 0,
                        })
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Per-membership pairing % at distribution: one rate per receiver tier. A tier
 * left blank earns no pairing income (its legs just bank). Writes
 * `pairing_pct_by_tier` keyed on tier code.
 */
function PairingPctByTierEditor({
  value,
  onChange,
  disabled,
}: {
  value: Record<string, number>;
  onChange: (next: Record<string, number>) => void;
  disabled?: boolean;
}) {
  const tiersQ = useQuery({ queryKey: ['admin-memberships'], queryFn: listMemberships });
  if (tiersQ.isLoading) return <Skeleton className="h-32 w-full" />;
  const tiers = tiersQ.data ?? [];
  if (tiers.length === 0) {
    return (
      <p className="text-xs text-muted-foreground border rounded-md p-4">
        No membership tiers configured yet. Create them under Members → Memberships first.
      </p>
    );
  }

  function writeRate(code: string, raw: string) {
    const next = { ...value };
    if (raw === '') delete next[code];
    else next[code] = Number(raw) || 0;
    onChange(next);
  }

  return (
    <div className="overflow-x-auto border rounded-md">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs text-left text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">Membership tier</th>
            <th className="px-3 py-2 font-medium">Pairing %</th>
          </tr>
        </thead>
        <tbody>
          {tiers.map((t) => (
            <tr key={t.code} className="border-t">
              <td className="px-3 py-1.5 text-xs font-mono" title={t.name}>
                {t.code}
              </td>
              <td className="px-2 py-1.5">
                <input
                  type="number"
                  className="h-8 w-24 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                  placeholder="0"
                  value={value[t.code] ?? ''}
                  disabled={disabled}
                  step={0.01}
                  min={0}
                  max={100}
                  onChange={(e) => writeRate(t.code, e.target.value)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PairingCombinationsEditor({
  value,
  onChange,
  disabled,
}: {
  value: Array<{ points: number; payout: number }>;
  onChange: (next: Array<{ points: number; payout: number }>) => void;
  disabled?: boolean;
}) {
  function update(i: number, patch: Partial<{ points: number; payout: number }>) {
    onChange(value.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }
  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...value, { points: 0, payout: 0 }]);
  }

  return (
    <div className="space-y-2">
      <table className="w-full text-sm border rounded-md overflow-hidden">
        <thead className="text-left text-muted-foreground bg-muted/40 text-xs">
          <tr>
            <th className="px-3 py-2 font-medium">Points per leg</th>
            <th className="px-3 py-2 font-medium">Payout</th>
            <th className="px-3 py-2 w-12"></th>
          </tr>
        </thead>
        <tbody>
          {value.length === 0 ? (
            <tr>
              <td colSpan={3} className="px-3 py-4 text-xs text-muted-foreground text-center">
                No combinations. Click "Add combination" to define one (or leave empty to use the
                single rule below).
              </td>
            </tr>
          ) : (
            value.map((row, i) => (
              <tr key={i} className="border-t">
                <td className="px-3 py-2">
                  <input
                    type="number"
                    className="h-8 w-32 px-2 rounded-md border bg-background text-sm disabled:opacity-50"
                    value={row.points}
                    disabled={disabled}
                    min={0}
                    step={1}
                    onChange={(e) => update(i, { points: Number(e.target.value) || 0 })}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    className="h-8 w-32 px-2 rounded-md border bg-background text-sm disabled:opacity-50"
                    value={row.payout}
                    disabled={disabled}
                    min={0}
                    step={0.01}
                    onChange={(e) => update(i, { payout: Number(e.target.value) || 0 })}
                  />
                </td>
                <td className="px-3 py-2">
                  {!disabled ? (
                    <button
                      type="button"
                      onClick={() => remove(i)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                      aria-label={`Remove combination ${i + 1}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {!disabled ? (
        <Button size="sm" variant="outline" onClick={add}>
          <Plus className="h-3 w-3 mr-1" />
          Add combination
        </Button>
      ) : null}
    </div>
  );
}

export type PairDropRow = NonNullable<BinaryConfig['pair_drops']>[number];

export function PairDropsEditor({
  value,
  onChange,
  disabled,
}: {
  value: PairDropRow[];
  onChange: (next: PairDropRow[]) => void;
  disabled?: boolean;
}) {
  function update(i: number, patch: Partial<PairDropRow>) {
    onChange(value.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }
  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...value, { n: 1, cash: 0, gc: 0, cash_capped: false, pairing_on_trigger: 'keep' }]);
  }

  // Flag duplicate N — each N maps to one ledger line (level=n); duplicates
  // would collide (last-wins on save). The matrix counts occurrences per N.
  const nCounts = new Map<number, number>();
  for (const r of value) nCounts.set(r.n, (nCounts.get(r.n) ?? 0) + 1);
  const hasDuplicateN = [...nCounts.values()].some((c) => c > 1);

  return (
    <div className="space-y-2">
      <table className="w-full text-sm border rounded-md overflow-hidden">
        <thead className="text-left text-muted-foreground bg-muted/40 text-xs">
          <tr>
            <th className="px-3 py-2 font-medium">Every N pairs</th>
            <th className="px-3 py-2 font-medium">Cash</th>
            <th className="px-3 py-2 font-medium">GC</th>
            <th className="px-3 py-2 font-medium">Within cash cap?</th>
            <th className="px-3 py-2 font-medium">Pairing on trigger</th>
            <th className="px-3 py-2 w-12"></th>
          </tr>
        </thead>
        <tbody>
          {value.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-3 py-4 text-xs text-muted-foreground text-center">
                No pair drops. Click "Add drop" to define one (e.g. N=1 GC=10 for every pair, N=5
                GC=50 for a 5th-pair milestone).
              </td>
            </tr>
          ) : (
            value.map((row, i) => {
              const cashOff = !(Number(row.cash) > 0);
              const dup = (nCounts.get(row.n) ?? 0) > 1;
              return (
                <tr key={i} className="border-t align-middle">
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      className={`h-8 w-24 px-2 rounded-md border bg-background text-sm disabled:opacity-50 ${dup ? 'border-destructive' : ''}`}
                      value={row.n}
                      disabled={disabled}
                      min={1}
                      step={1}
                      onChange={(e) => update(i, { n: Math.max(1, Number(e.target.value) || 1) })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      className="h-8 w-28 px-2 rounded-md border bg-background text-sm disabled:opacity-50"
                      value={row.cash ?? 0}
                      disabled={disabled}
                      min={0}
                      step={0.01}
                      onChange={(e) => update(i, { cash: Number(e.target.value) || 0 })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      className="h-8 w-28 px-2 rounded-md border bg-background text-sm disabled:opacity-50"
                      value={row.gc ?? 0}
                      disabled={disabled}
                      min={0}
                      step={0.01}
                      onChange={(e) => update(i, { gc: Number(e.target.value) || 0 })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 disabled:opacity-50"
                      checked={!cashOff && row.cash_capped === true}
                      disabled={disabled || cashOff}
                      title={
                        cashOff
                          ? 'Set a cash amount to choose cap behavior'
                          : 'On = drawn from the daily cash cap (can be flushed); off = paid on top, never capped'
                      }
                      onChange={(e) => update(i, { cash_capped: e.target.checked })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="h-8 px-2 rounded-md border bg-background text-sm disabled:opacity-50"
                      value={row.pairing_on_trigger ?? 'keep'}
                      disabled={disabled}
                      onChange={(e) =>
                        update(i, {
                          pairing_on_trigger: e.target.value as PairDropRow['pairing_on_trigger'],
                        })
                      }
                    >
                      <option value="keep">Keep pairing</option>
                      <option value="suppress">Suppress pairing</option>
                      <option value="to_gc">Convert pairing → GC</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    {!disabled ? (
                      <button
                        type="button"
                        onClick={() => remove(i)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        aria-label={`Remove drop ${i + 1}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {hasDuplicateN ? (
        <p className="text-[10px] text-destructive">
          Each N must be unique — duplicate rows are highlighted. On save, the last row with a given
          N wins.
        </p>
      ) : null}
      <p className="text-[10px] text-muted-foreground">
        "Pairing on trigger" changes the normal pairing payout on the Nth pair: Keep = milestone
        stacks on top; Suppress = only the milestone pays; Convert → GC = the pairing amount is paid
        as GC (1:1) instead of cash. When several rows hit the same pair, the most restrictive wins
        (suppress &gt; convert &gt; keep).
      </p>
      {!disabled ? (
        <Button size="sm" variant="outline" onClick={add}>
          <Plus className="h-3 w-3 mr-1" />
          Add drop
        </Button>
      ) : null}
    </div>
  );
}

function PointsMatrixEditor({
  value,
  onChange,
  disabled,
}: {
  value: Record<string, Record<string, number>>;
  onChange: (next: Record<string, Record<string, number>>) => void;
  disabled?: boolean;
}) {
  const tiersQ = useQuery({ queryKey: ['admin-memberships'], queryFn: listMemberships });
  if (tiersQ.isLoading) return <Skeleton className="h-40 w-full" />;
  const tiers = tiersQ.data ?? [];
  if (tiers.length === 0) {
    return (
      <p className="text-xs text-muted-foreground border rounded-md p-4">
        No membership tiers configured yet. Create them under Members → Memberships first.
      </p>
    );
  }

  function setCell(receiver: string, buyer: string, n: number) {
    const next: Record<string, Record<string, number>> = {};
    for (const r of Object.keys(value)) next[r] = { ...value[r]! };
    if (!next[receiver]) next[receiver] = {};
    if (n === 0) {
      delete next[receiver]![buyer];
      if (Object.keys(next[receiver]!).length === 0) delete next[receiver];
    } else {
      next[receiver]![buyer] = n;
    }
    onChange(next);
  }

  return (
    <div className="space-y-2">
      <p className="text-[10px] text-muted-foreground">
        Rows = <strong>receiver tier</strong> (the ancestor earning the points). Columns ={' '}
        <strong>buyer tier</strong> (the slot being placed). Cell = points credited to that
        ancestor's leg.
      </p>
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/40 text-xs text-left text-muted-foreground">
              <th className="px-3 py-2 font-medium sticky left-0 bg-muted/40">Receiver \ Buyer</th>
              {tiers.map((t) => (
                <th key={t.code} className="px-3 py-2 font-mono font-medium" title={t.name}>
                  {t.code}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tiers.map((receiver) => (
              <tr key={receiver.code} className="border-t">
                <td
                  className="px-3 py-1.5 text-xs font-mono font-medium bg-muted/20 sticky left-0"
                  title={receiver.name}
                >
                  {receiver.code}
                </td>
                {tiers.map((buyer) => {
                  const cell = value?.[receiver.code]?.[buyer.code] ?? 0;
                  return (
                    <td key={buyer.code} className="px-2 py-1.5">
                      <input
                        type="number"
                        className="h-8 w-20 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                        value={cell}
                        disabled={disabled}
                        step={1}
                        min={0}
                        onChange={(e) =>
                          setCell(receiver.code, buyer.code, Number(e.target.value) || 0)
                        }
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type MentorTiers = NonNullable<NonNullable<BinaryConfig['mentor_bonus']>['tiers']>;

/**
 * Round-robin mentor-bonus matrix editor. Level tabs select a sponsor level;
 * the table shows per-tier Percent % + Min directs for that level. Rows =
 * membership tiers (the mentor's tier). Writes `mentor_bonus.tiers`.
 */
function MentorBonusMatrixEditor({
  value,
  onChange,
  disabled,
}: {
  value: MentorTiers;
  onChange: (next: MentorTiers) => void;
  disabled?: boolean;
}) {
  const tiersQ = useQuery({ queryKey: ['admin-memberships'], queryFn: listMemberships });

  const presentLevels = useMemo(() => {
    const set = new Set<number>();
    for (const tier of Object.values(value)) {
      for (const k of Object.keys(tier.amount ?? {})) {
        const n = Number(k);
        if (Number.isFinite(n) && n >= 1) set.add(n);
      }
    }
    if (set.size === 0) set.add(1);
    return Array.from(set).sort((a, b) => a - b);
  }, [value]);

  const [extra, setExtra] = useState<number[]>([]);
  const levels = useMemo(() => {
    const set = new Set<number>(presentLevels);
    for (const n of extra) set.add(n);
    return Array.from(set).sort((a, b) => a - b);
  }, [presentLevels, extra]);
  const [active, setActive] = useState<number>(levels[0] ?? 1);

  if (tiersQ.isLoading) return <Skeleton className="h-40 w-full" />;
  const tiers = tiersQ.data ?? [];
  if (tiers.length === 0) {
    return (
      <p className="text-xs text-muted-foreground border rounded-md p-4">
        No membership tiers configured yet. Create them under Members → Memberships first.
      </p>
    );
  }

  function getVal(code: string, mapKey: 'amount' | 'requirement', level: number): number {
    const v = value[code]?.[mapKey]?.[String(level)];
    return typeof v === 'number' ? v : Number(v) || 0;
  }
  function setVal(code: string, mapKey: 'amount' | 'requirement', level: number, n: number) {
    const next: MentorTiers = {};
    for (const c of Object.keys(value)) {
      next[c] = {
        amount: { ...(value[c]!.amount ?? {}) },
        requirement: { ...(value[c]!.requirement ?? {}) },
      };
    }
    if (!next[code]) next[code] = { amount: {}, requirement: {} };
    const map = { ...(next[code]![mapKey] ?? {}) };
    if (n === 0) delete map[String(level)];
    else map[String(level)] = n;
    next[code]![mapKey] = map;
    // Drop empty maps / tiers so the stored config stays sparse.
    for (const c of Object.keys(next)) {
      const t = next[c]!;
      if (t.amount && Object.keys(t.amount).length === 0) delete t.amount;
      if (t.requirement && Object.keys(t.requirement).length === 0) delete t.requirement;
      if (!t.amount && !t.requirement) delete next[c];
    }
    onChange(next);
  }

  function addLevel() {
    const nextLevel = Math.max(...levels, 0) + 1;
    setExtra((prev) => [...prev, nextLevel]);
    setActive(nextLevel);
  }
  function removeLevel(level: number) {
    for (const t of tiers) {
      setVal(t.code, 'amount', level, 0);
      setVal(t.code, 'requirement', level, 0);
    }
    setExtra((prev) => prev.filter((l) => l !== level));
    if (active === level) setActive(levels.filter((l) => l !== level)[0] ?? 1);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 flex-wrap">
        {levels.map((lvl) => (
          <button
            key={lvl}
            type="button"
            onClick={() => setActive(lvl)}
            className={cn(
              'h-8 px-3 rounded-md text-xs font-mono border transition-colors',
              active === lvl
                ? 'bg-primary/10 border-primary text-primary'
                : 'border-input text-muted-foreground hover:bg-muted',
            )}
          >
            {lvl === 1 ? 'Level 1 (direct)' : `Level ${lvl}`}
          </button>
        ))}
        {!disabled ? (
          <>
            <Button size="sm" variant="outline" onClick={addLevel}>
              <Plus className="h-3 w-3 mr-1" />
              Level
            </Button>
            {levels.length > 1 ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removeLevel(active)}
                className="text-destructive"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Remove L{active}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-left text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Mentor tier</th>
              <th className="px-3 py-2 font-medium">Percent %</th>
              <th className="px-3 py-2 font-medium">Min directs</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((t) => (
              <tr key={t.code} className="border-t">
                <td className="px-3 py-1.5 text-xs font-mono" title={t.name}>
                  {t.code}
                </td>
                <td className="px-2 py-1.5">
                  <input
                    type="number"
                    className="h-8 w-24 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                    value={getVal(t.code, 'amount', active)}
                    disabled={disabled}
                    step={0.01}
                    min={0}
                    onChange={(e) => setVal(t.code, 'amount', active, Number(e.target.value) || 0)}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <input
                    type="number"
                    className="h-8 w-24 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                    value={getVal(t.code, 'requirement', active)}
                    disabled={disabled}
                    step={1}
                    min={0}
                    onChange={(e) =>
                      setVal(t.code, 'requirement', active, Number(e.target.value) || 0)
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function emptyBinaryConfig(): BinaryConfig {
  return {
    pairing: { unit_points: 100, pay_per_pair: 50 },
    cycle: { cycles: 1, daily_cap: 10_000 },
    pair_drops: [],
    timezone: 'Asia/Manila',
    points_matrix: {},
  };
}

export function emptyTeslabConfig(): BinaryConfig {
  return {
    ...emptyBinaryConfig(),
    pairing_pct_by_tier: {},
    qualification: { required_ppv: 0 },
    total_flushout: false,
    strong_leg_retention: true,
    product_points_on_purchase: true,
    teslab_mentor: { enabled: false, tiers: {} },
  };
}

export function emptyExtremeConfig(): BinaryConfig {
  return {
    ...emptyBinaryConfig(),
    // Extreme pairing is defined PER MEMBERSHIP — clear the flat fallback so a
    // tier with no per-tier pairing table earns nothing (rather than silently
    // falling back to a global rate). Pairing, GC riders, and total_flushout all
    // live in the Memberships / per-tier editor.
    pairing: { pay_per_pair: 0 },
    pair_drops: [],
    direct_requirements: 2,
  };
}
